import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import { useAppDispatch } from "app/hooks";
import { setCustomMessage } from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { PAYWALL_BYPASS } from "constants/feature-flags";
import { useLazyGetCurrentUserPaywallFeaturesQuery } from "services/paywall";
import type { PaywallFeaturesStatus } from "services/types/paywall.type";

export default function useCheckPaywallFeatureAvailability() {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const paywallBypassEnabled = flags[PAYWALL_BYPASS] ?? true;

  const [fetchPaywallFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();

  const checkFeatureAvailability = useCallback(
    (featureKey: keyof PaywallFeaturesStatus) => {
      return fetchPaywallFeatures().then((response) => {
        if ("data" in response) {
          if (paywallBypassEnabled) {
            return {
              isAvailable: true,
              isInTrial: false
            };
          }

          const feature = response.data?.[featureKey];

          if (feature) {
            let isInTrial = false;
            let isAvailable = Boolean(feature.active);

            if (isAvailable && feature.trial_date) {
              let isTrialExpired = false;

              try {
                isTrialExpired = new Date(feature.trial_date) <= new Date();
              } catch {}

              isInTrial = !isTrialExpired;
            }

            return {
              isAvailable,
              isInTrial
            };
          }
        } else if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
        }
        return {
          isAvailable: false,
          isInTrial: false
        };
      });
    },
    [dispatch, fetchPaywallFeatures, paywallBypassEnabled]
  );

  return checkFeatureAvailability;
}
